import { Button as MUIButton } from "@mui/material";
import Backbutton from "assets/backbutton";
import Button from "components/common/Button";
import Typography from "components/common/Typography";
import { isUndefined } from "lodash";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { includeAboveProperty, sourceValues } from "utils/CommonConstants";
import { getHltInncodes, getUserTypeValue, isAboveUserPlanBookmarked, refreshToken } from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { getAssignmentPayload, shouldDisplayPreview } from "./Helper";
import { assignPlanAsync, handleBack, handleNext } from "./PlanAssignment.slice";

const AssignmentFooter = (props: any) => {
  const dispatch = useAppDispatch();
  const { onClose, scListId, isAssignCopy, sharedSearch } = props;
  const isAbovePropertyUser = useAppSelector((state) => state.app.loggedInUserDetails.isAbovePropertyUser);
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const flow: any = useAppSelector((state) => state.PlanAssignment.flow);
  const previewList = useAppSelector((state) => state.PlanAssignment.previewList);
  const { currentStep, payloadData, isErrorWhenLimitExceed, isErrorWhenPlanSelectionExceed } = useAppSelector(
    (state) => state.PlanAssignment
  );
  const totalMemberSelection = useAppSelector((state) => state.PlanAssignment.totalMemberSelection);
  const unselecteMember = useAppSelector((state) => state.PlanAssignment.unselecteMember);
  const memberSelection = useAppSelector((state) => state.PlanAssignment.memberSelection);
  const isAllMemberSelected = useAppSelector((state) => state.PlanAssignment.isAllMemberSelected);
  const { propertyList } = useAppSelector((state) => state.aboveProperty);
  const filters = useAppSelector((state: any) => state.PlanAssignment.memberSelection.filters);
  const { brand, region } = useAppSelector((state) => state.PlanAssignment.planSelection.filters);

  const isDisplayName = payloadData?.users.length
    ? getUserNameWithConditionalCheck(
        payloadData?.users[0]?.displayName,
        payloadData?.users[0]?.firstName,
        payloadData?.users[0]?.lastName
      )
    : false;
  const isUserDisabled = payloadData?.users?.hltId?.length || isDisplayName;

  const isDisabled = () => {
    if (flow) {
      switch (flow[currentStep]) {
        case "Plan Selection":
          return payloadData?.plans?.length;
        case "Assignment Preview":
          return true;
        case "Member Selection":
          return isUserDisabled;
        case "Assignment Details":
          return true;
        case "Confirmation":
          return true;
        default:
          return false;
      }
    }
    return false;
  };

  const allMemberCount = () => {
    return totalMemberSelection - unselecteMember?.length;
  };

  const showCount = () => {
    if (flow) {
      switch (flow[currentStep]) {
        case "Plan Selection":
          return payloadData?.plans?.length;
        case "Member Selection":
          return isAllMemberSelected
            ? allMemberCount()
            : payloadData?.users?.length ?? payloadData?.users?.mappings?.length ?? 0;

        default:
          return 0;
      }
    }
    return false;
  };

  const handleAssignPlan = async () => {
    await refreshToken();
    const payload: any = getAssignmentPayload({ ...props, source: getSourceBasedOnUserAndFlow() }, previewList);
    const mappedDelectedMember = unselecteMember?.map((data: any) => {
      return { inncode: data.innCodes[0], hltId: data.hltId };
    });
    const allInncodes = propertyList?.map((data: any) => data.innCode);
    const inncodesList = memberSelection?.filters?.property?.length ? memberSelection?.filters?.property : allInncodes;
    const { inncodeUsers, ...restPayload } = payload;
    const meta = props?.isPlanProgress ? props?.plans?.[0]?.meta : {};
    const modifiedPayload = isAllMemberSelected
      ? {
          ...restPayload,
          deSelectedInncodeUsers: mappedDelectedMember,
          userSearchInternalRequest: {
            ignoreNonFilteredInnCodes: true,
            respondWithHltIdAndInnCode: true,
            includecorpusers: false,
            roles: memberSelection?.filters?.role,
            searchText: memberSelection?.filters?.searchText,
            innCodes: props.inncodes?.length
              ? props.inncodes
              : filters?.property?.length
              ? filters.property
              : !isUndefined(getHltInncodes(true))
              ? getHltInncodes(true)?.split(",")
              : [],
            userType: getUserTypeValue(memberSelection?.filters[includeAboveProperty]),
            ...(meta?.brandCodes?.length
              ? { brandCodes: meta.brandCodes }
              : brand?.length
              ? { brandCodes: brand }
              : {}),
            ...(meta?.regions?.length
              ? { regions: meta.regions?.join("::") }
              : region?.length
              ? { regions: region?.join("::") }
              : {}),
            ...(meta?.locationTypes?.length ? { locationTypes: meta.locationTypes } : {}),
            ...(meta?.innCodes?.length ? { tgInnCodes: meta.innCodes } : {}),
            ...(meta?.managementTypes?.length ? { mgmtTypes: meta.managementTypes } : {})
          },
          isAllUsersSelected: true
        }
      : payload;
    dispatch(assignPlanAsync(modifiedPayload));
  };

  const getSourceBasedOnUserAndFlow = () => {
    const isBookmarked = isAbovePropertyUser
      ? isAboveUserPlanBookmarked(
          payloadData?.plans[0]?.id,
          props?.isTemplatePlan ? "bookmarkedTemplate" : "bookmarkedIdList"
        )
      : payloadData?.plans[0]?.isBookmarked;

    if (sharedSearch) return sourceValues?.sharedSearch;
    if (isCorp) {
      return isBookmarked ? sourceValues.consultantBookmark : sourceValues.assignedSme;
    }

    if (isAbovePropertyUser) {
      return isBookmarked ? sourceValues.managementBookmark : "management_assigned";
    }

    return isBookmarked ? sourceValues.leaderBookmark : "leader_assigned";
  };

  const handleNextClick = async () => {
    if (
      (flow[currentStep + 1] === "Assignment Details" || flow[currentStep + 1] === "Assignment Preview") &&
      flow[currentStep] !== "Assignment Preview" &&
      !isAssignCopy
    ) {
      const payload = await getAssignmentPayload({ ...props, source: getSourceBasedOnUserAndFlow() });
      shouldDisplayPreview(
        dispatch,
        props.flow,
        payload,
        props.inncodes,
        props?.isPlanProgress,
        props?.plans?.[0]?.meta
      );
    }
    dispatch(handleNext());
  };

  return (
    <>
      <div>
        {currentStep !== 0 && (
          <MUIButton
            sx={{ paddingLeft: "0px", gap: "8px", marginY: { xs: "0", md: "unset" } }}
            variant="text"
            onClick={() => dispatch(handleBack())}
            disabled={currentStep === 0}
          >
            <Backbutton color={tokens.Text.General.Color}></Backbutton>
            <Typography color={tokens.Text.General.Color} children="Back" variant="body1" />
          </MUIButton>
        )}
      </div>
      <div>
        <div className="left-action-item">
          {scListId && flow[currentStep] === "Plan Selection" && payloadData?.users?.hltId?.length > 0 && (
            <Typography
              variant={"body2"}
              color={tokens.Icon.General.Secondary}
              children={`${payloadData?.users?.hltId?.length} Inncode-Members selected`}
            ></Typography>
          )}
          <Button variant="secondary" label="Cancel" onClick={onClose} className="cancel-footer" />
          {currentStep === flow?.length - 1 ? (
            <Button label="Assign" onClick={handleAssignPlan} />
          ) : (
            <Button
              label={flow[currentStep] === "Assignment Preview" ? "Proceed With Selections" : "Next"}
              endIcon={
                showCount() > 0 && (
                  <div className="next-count">
                    <Typography className="count-badge" variant={"body2"} showBold>
                      {showCount()}
                    </Typography>
                  </div>
                )
              }
              onClick={handleNextClick}
              disabled={!isDisabled() || isErrorWhenLimitExceed || isErrorWhenPlanSelectionExceed}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AssignmentFooter;
